// ============================================================================
// AffichagesPage — liste des affichages (E1d, F-300..F-303) — /affichages
// ============================================================================
// Portage du PoC (dashboard)/affichages/page.tsx au standard maison :
//   - lecture OUVERTE a tout role GEST interne (M-302) ; le 403 corporate-sans-GEST
//     (Q1) donne l'ecran de refus i18n ;
//   - filtre Ouverts/Tous en GsSegmented deep-linkable ?status=all (F-300) ;
//   - alerte ?error= mappee i18n (F-301, GA-21) ;
//   - « Demande d'affichage » (F-303) si writableTeams.length>0 ;
//   - grille cartes metier + drill-in (GEST CARTES METIER TEMPS 2) ;
//   - etats skeleton / empty / error / forbidden (patterns E1b/E3a).

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageHeader, EmptyState, GsSegmented, SkeletonCard, QueryError,
  GS_COMPONENTS,
} from '@gs/ui';
import { ClipboardList, Plus, ShieldAlert } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { usePostings } from '../../hooks/usePostings';
import { NewPostingDialog } from '../../components/affichages/NewPostingDialog';
import { PostingOverviewCard } from '../../components/affichages/PostingOverviewCard';

const KNOWN_ERRORS = ['invalid', 'forbidden', 'notfound', 'server'];

/** Ecran de refus (Q1 corporate-sans-GEST) — l'autorite est le 403 serveur. */
export function PostingsForbidden() {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<ShieldAlert size={48} aria-hidden="true" />}
      title={t('gest:affichages.forbidden.title')}
      description={t('gest:affichages.forbidden.description')}
    />
  );
}

export default function AffichagesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dialogOpen, setDialogOpen] = useState(false);

  const showAll = searchParams.get('status') === 'all';
  const errorCode = searchParams.get('error');

  const listQ = usePostings(showAll, !!user);
  const forbidden = listQ.isError && listQ.error instanceof ApiForbiddenError;

  function setError(code: string) {
    const next = new URLSearchParams(searchParams);
    next.set('error', code);
    setSearchParams(next);
  }

  function setStatusFilter(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.set('status', 'all');
    else next.delete('status');
    next.delete('error');
    setSearchParams(next);
  }

  const canCreate = !!listQ.data && listQ.data.writableTeams.length > 0;

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:affichages.title')}
        onRefresh={() => void listQ.refetch()}
        refreshLoading={listQ.isFetching}
        actions={
          <GsSegmented
            ariaLabel={t('gest:affichages.filter.label')}
            value={showAll ? 'all' : 'open'}
            onChange={setStatusFilter}
            options={[
              { value: 'open', label: t('gest:affichages.filter.open') },
              { value: 'all', label: t('gest:affichages.filter.all') },
            ]}
          />
        }
      />

      {errorCode ? (
        <div
          role="alert"
          className="rounded-xl border border-slate-200 border-l-3 border-l-red-500 bg-white px-4 py-3 text-field text-red-700 dark:border-slate-700 dark:border-l-red-500 dark:bg-slate-800 dark:text-red-300"
        >
          {t(`gest:affichages.errors.${KNOWN_ERRORS.includes(errorCode) ? errorCode : 'server'}`)}
        </div>
      ) : null}

      {forbidden ? (
        <PostingsForbidden />
      ) : listQ.isLoading ? (
        <div className="self-start grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : listQ.isError ? (
        <QueryError message={t('gest:affichages.loadError')} onRetry={() => void listQ.refetch()} />
      ) : listQ.data ? (
        <>
          {canCreate ? (
            <div>
              <button
                type="button"
                onClick={() => setDialogOpen(true)}
                className={`${GS_COMPONENTS.btnAction} inline-flex min-h-11 items-center gap-1.5 touch-manipulation`}
              >
                <Plus size={16} aria-hidden="true" />
                {t('gest:affichages.new')}
              </button>
            </div>
          ) : null}

          {listQ.data.postings.length === 0 ? (
            <EmptyState
              icon={<ClipboardList size={48} aria-hidden="true" />}
              title={showAll ? t('gest:affichages.emptyAll') : t('gest:affichages.emptyOpen')}
            />
          ) : (
            <div
              className="self-start grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
              role="list"
            >
              {listQ.data.postings.map((posting) => (
                <PostingOverviewCard
                  key={posting.id}
                  posting={posting}
                  onOpen={(id) => navigate(`/affichages/${id}`)}
                />
              ))}
            </div>
          )}
        </>
      ) : null}

      {canCreate ? (
        <NewPostingDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onError={setError}
          writableTeams={listQ.data?.writableTeams ?? []}
        />
      ) : null}
    </div>
  );
}
